/**
 * Utility functions for visit time handling
 * Shared between Visits page, Calendar page, and other visit-related components
 */

import type { Dayjs } from "dayjs";
import { message } from "antd";
import {
  visitNotesController,
  AuthService,
  APIProvider,
  timeProvider,
} from "../AppController";
import { APIRoutes } from "../apiRoutes";

/**
 * Get the effective date for a visit note.
 *
 * Priority:
 *   1. notes.scheduledDate              (authoritative scheduled time, written for third_party_np_visit)
 *   2. context.schedule.scheduled_date  (new structure written by calendar/NewVisit for other types)
 *   3. context.scheduled_date           (legacy root-level context)
 *   4. scheduled_date                   (MySQL virtual column, present on third_party_np_visit)
 *   5. created_at                       (fallback)
 *
 * @param note - The visit note object (notes/context may be a JSON string or parsed object)
 * @returns The effective date string for sorting/display
 */
export const getEffectiveVisitDate = (note: any): string => {
  // Parse notes JSON (may arrive as a string from the API)
  let notesObj: Record<string, any> = {};
  try {
    notesObj =
      typeof note.notes === "string"
        ? JSON.parse(note.notes)
        : (note.notes as Record<string, any>) ?? {};
  } catch {
    // ignore parse errors
  }

  if (notesObj.scheduledDate) {
    return notesObj.scheduledDate;
  }

  // Parse context JSON (may arrive as a string from the API)
  let ctx: Record<string, any> = {};
  try {
    ctx =
      typeof note.context === "string"
        ? JSON.parse(note.context)
        : (note.context as Record<string, any>) ?? {};
  } catch {
    // ignore parse errors
  }

  const scheduleDate =
    (ctx.schedule as Record<string, any> | undefined)?.scheduled_date ||
    ctx.scheduled_date ||
    note.scheduled_date;

  return scheduleDate || note.created_at || "";
};

/**
 * Determine whether a note was imported from Compassus.
 *
 * Two API shapes exist:
 *  - List endpoint  (/patient/{id}/notes):    top-level `source: "Compassus"`
 *  - Single endpoint (/visit-notes/{id}):     nested    `notes.source: "compassus"` (lowercase)
 *
 * This helper handles both cases case-insensitively.
 */
export const isCompassusNote = (note: any): boolean => {
  const topLevel = note?.source;
  if (typeof topLevel === "string" && topLevel.toLowerCase() === "compassus") {
    return true;
  }
  // notes can be a pre-parsed object (list endpoint) or a JSON string (single-note endpoint)
  let notesObj = note?.notes;
  if (typeof notesObj === "string") {
    try {
      notesObj = JSON.parse(notesObj);
    } catch {
      return false;
    }
  }
  const nested =
    typeof notesObj === "object" && notesObj !== null
      ? notesObj.source
      : undefined;
  return typeof nested === "string" && nested.toLowerCase() === "compassus";
};

/**
 * True for visit-notes list items that are not Compassus-imported (native Circadia, or legacy rows with no source).
 */
export const isCircadiaVisitNoteSource = (note: any): boolean =>
  !isCompassusNote(note);

/** UI: show "Circadia" tag for linked IPV rows; hide for calendar-only external events. */
export function visitNoteRowShowsCircadiaTag(visit: {
  isExternal?: boolean;
  linkedVisit?: { isCircadiaSource?: boolean } | null;
  isCircadiaSource?: boolean;
}): boolean {
  if (visit.isExternal && !visit.linkedVisit) return false;
  const n = visit.linkedVisit ?? visit;
  return n.isCircadiaSource === true;
}

/**
 * Convert a visit's date string into a facility-local Dayjs object.
 *
 * Compassus-imported notes store dates as local facility time already,
 * so they must NOT go through the UTC→facility conversion.
 * All other notes are stored as UTC and must be converted via convertIntoLocalDateTime.
 *
 * @param visitDate - The date string (ISO or "YYYY-MM-DD HH:mm:ss")
 * @param isCompassusSync - True when the note came from Compassus (use isCompassusNote())
 */
export const getVisitDateTime = (
  visitDate: string | undefined | null,
  isCompassusSync: boolean | undefined
) => {
  if (!visitDate) return undefined;
  if (isCompassusSync) {
    const d = timeProvider.dateLibrary(visitDate);
    return d.isValid() ? d : undefined;
  }
  return timeProvider.convertIntoLocalDateTime(visitDate);
};

/**
 * Build the date update payload for a visit note PATCH request.
 * HomeHealth notes update notes.scheduledDate/dateOfService.
 * Other visit types update created_at.
 *
 * @param docType - The document type (e.g., "third_party_np_visit")
 * @param dateTime - The new date/time string (ISO format)
 * @returns Partial payload object to spread into the PATCH request
 */
export const buildDateUpdatePayload = (
  docType: string | undefined,
  dateTime: string
): Record<string, any> => {
  const isHomeHealth = docType === "third_party_np_visit";

  if (isHomeHealth) {
    const formattedDateTime = timeProvider
      .dateLibrary(dateTime)
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");
    return {
      notes: {
        scheduledDate: formattedDateTime,
        dateOfService: formattedDateTime,
        utcTimezone: true,
      },
    };
  }

  return { created_at: dateTime };
};

// Visit note interface for the update function
export interface IVisitNoteForUpdate {
  id: number;
  patientId: number;
  patientName: string;
  visitDate?: string;
  context?: Record<string, any>;
}

export interface IUpdateVisitTimeParams {
  visit: IVisitNoteForUpdate;
  newDate: Dayjs;
  clinicId: number;
  clinicTimezone?: string | null;
  hpiTemplates?: Array<{ id: number; name: string }>;
  onSuccess?: (updatedVisit: IVisitNoteForUpdate) => void;
  docType?: string; // Optional: overrides doc_type from fetched note. If omitted, derived from note data.
}

export interface IUpdateVisitTimeResult {
  success: boolean;
  updatedVisit?: IVisitNoteForUpdate;
  error?: string;
}

/**
 * Update the time for an upcoming visit
 * Handles ICS invitation updates if recipients exist
 * For HomeHealth (third_party_np_visit), updates notes.scheduledDate/dateOfService instead of created_at
 * docType is derived from the fetched note if not provided, ensuring correct behavior even if caller omits it
 */
export const updateVisitTime = async ({
  visit,
  newDate,
  clinicId,
  clinicTimezone,
  hpiTemplates,
  onSuccess,
  docType,
}: IUpdateVisitTimeParams): Promise<IUpdateVisitTimeResult> => {
  if (!clinicId || !newDate) {
    console.warn("[UpdateVisitTime] Missing clinicId or newDate", {
      clinicId,
      newDate,
    });
    return { success: false, error: "Missing required parameters" };
  }

  console.warn("[UpdateVisitTime] Starting update for visit:", {
    visitId: visit.id,
    patientId: visit.patientId,
    patientName: visit.patientName,
  });

  try {
    const authToken = await AuthService.getAuthenticationToken();
    const newStartDateTime = newDate.toISOString();
    // Default to 1 hour duration
    const newEndDateTime = newDate.add(1, "hour").toISOString();

    // Fetch the full note to get the context (getAllVisitNotes doesn't include context)
    console.warn("[UpdateVisitTime] Fetching full note details...");
    const noteResult = await visitNotesController.getVisitNote(visit.id);

    let context: any = {};
    if (noteResult.success && noteResult.data) {
      const noteData = noteResult.data;
      // Parse context if it's a string
      if (typeof noteData.context === "string") {
        try {
          context = JSON.parse(noteData.context);
        } catch {
          console.warn("[UpdateVisitTime] Failed to parse context string");
          context = {};
        }
      } else {
        context = noteData.context || {};
      }
    }

    // Derive effective docType from caller-provided value or fetched note data
    // This ensures HomeHealth notes reliably update notes.scheduledDate/dateOfService
    // even if caller omits docType
    const effectiveDocType = docType ?? noteResult.data?.doc_type;

    console.warn("[UpdateVisitTime] Fetched context:", context);

    // Check for schedule data in new structure (context.schedule) or legacy structure (context root)
    const scheduleContext = context.schedule || {};
    const existingIcsUid = scheduleContext.ics_uid || context.ics_uid;
    const existingSequence =
      typeof scheduleContext.ics_sequence === "number"
        ? scheduleContext.ics_sequence
        : typeof context.ics_sequence === "number"
        ? context.ics_sequence
        : 0;
    const newSequence = existingSequence + 1;

    // Get recipients from new structure or legacy structure
    const recipients = scheduleContext.recipients || context.recipients;

    // Get other schedule data from new structure or legacy structure
    const templateId = scheduleContext.template_id || context.template_id;
    // Get template name from context, or look it up from templates list using templateId
    let templateName = scheduleContext.template_name || context.template_name;
    if (!templateName && templateId && hpiTemplates?.length) {
      const templateIdNum =
        typeof templateId === "string" ? parseInt(templateId, 10) : templateId;
      const foundTemplate = hpiTemplates.find((t) => t.id === templateIdNum);
      templateName = foundTemplate?.name;
    }
    // Use eventTitle from context, or fall back to template name (Visit Type)
    const eventTitle =
      scheduleContext.eventTitle || templateName || "Home Health Visit";
    const location = scheduleContext.location || context.location || "";

    console.warn("[UpdateVisitTime] ICS info:", {
      existingIcsUid,
      existingSequence,
      newSequence,
    });
    // Format recipients for the API - only include those with valid contact info
    // Recipients without any contacts (e.g. patient with no phone/email on file) are skipped
    const formattedRecipients =
      recipients && recipients.length > 0
        ? recipients
            .filter((r: any) => {
              const hasPhone = r.phone_numbers?.some((p: string) => p?.trim());
              const hasEmail = r.emails?.some((e: string) => e?.trim());
              return hasPhone || hasEmail;
            })
            .map((r: any) => {
              const result: any = { type: r.type };
              const validPhones = r.phone_numbers?.filter((p: string) =>
                p?.trim()
              );
              if (validPhones && validPhones.length > 0) {
                result.phone_numbers = validPhones;
              }
              const validEmails = r.emails?.filter((e: string) => e?.trim());
              if (validEmails && validEmails.length > 0) {
                result.emails = validEmails;
              }
              return result;
            })
        : [];

    // Determine if we should send reminder messages (only if recipients with valid contacts exist)
    const hasRecipients = formattedRecipients.length > 0;
    console.warn("[UpdateVisitTime] Recipients check:", {
      hasIcsUid: !!existingIcsUid,
      hasRecipients,
      totalRecipients: recipients?.length,
      recipientsWithContacts: formattedRecipients.length,
      willSendReminder: hasRecipients,
    });

    let updatedContext: any;

    // Send reschedule messages if there are recipients with valid contacts
    if (hasRecipients) {
      // Send reschedule notification via send-visit-reminder
      // If ics_uid exists: include ICS to update existing calendar invitation
      // If no ics_uid: just send text message without ICS (no duplicate calendar events)
      const hasExistingIcs = !!existingIcsUid;
      const reminderPayload: Record<string, any> = {
        patient_id: visit.patientId,
        patient_name: visit.patientName,
        template_id: templateId,
        event_title: eventTitle,
        start_datetime: newStartDateTime,
        end_datetime: newEndDateTime,
        location: location,
        recipients: formattedRecipients,
        ics_sequence: newSequence,
        is_reschedule: true,
        timezone: clinicTimezone || undefined,
        // Only include ICS calendar link if updating existing calendar event
        include_calendar_link: hasExistingIcs,
      };

      // Include ics_uid if it exists (to update existing calendar event)
      if (hasExistingIcs) {
        reminderPayload.ics_uid = existingIcsUid;
      }

      console.warn(
        "[UpdateVisitTime] Sending reschedule notification:",
        reminderPayload
      );

      const reminderResponse = await APIProvider.POST(
        APIRoutes.apiSendVisitReminder,
        reminderPayload,
        {
          Authorization: `Bearer ${authToken}`,
        }
      );

      if (reminderResponse.success || reminderResponse.status === true) {
        console.warn(
          "[UpdateVisitTime] Reschedule notification sent successfully"
        );

        // Build updated schedule data
        const updatedSchedule = {
          ...(scheduleContext || {}),
          scheduled_date: newStartDateTime,
          scheduled_end_date: newEndDateTime,
          timezone: clinicTimezone || undefined,
          // Use new ics_uid from response, or existing one, or undefined if neither
          ics_uid: reminderResponse.ics_uid || existingIcsUid,
          ics_url:
            reminderResponse.ics_url ||
            scheduleContext.ics_url ||
            context.ics_url,
          ics_content:
            reminderResponse.ics_content ||
            scheduleContext.ics_content ||
            context.ics_content,
          ics_sequence: newSequence,
          // Always store the FULL original recipients list (including those with empty contacts)
          // so that all recipients (e.g. patient) remain visible in the schedule tab for future edits.
          // The API only receives recipients with valid contacts, but we preserve the full list here.
          recipients: recipients,
          send_results: reminderResponse.results,
          send_message: reminderResponse.message,
        };

        // Remove old root-level schedule fields and use new structure
        const {
          scheduled_date: _sd,
          scheduled_end_date: _sed,
          ics_uid: _iu,
          ics_url: _iurl,
          ics_content: _ic,
          ics_sequence: _is,
          recipients: _r,
          send_results: _sr,
          send_message: _sm,
          scheduling: _sched,
          ...cleanContext
        } = context;

        updatedContext = {
          ...cleanContext,
          schedule: updatedSchedule,
        };

        // Update the note with new time and updated context
        const updateUrl = APIRoutes.apiUpdateVisitNote
          .replace("{clinic_id}", String(clinicId))
          .replace("{patient_id}", String(visit.patientId))
          .replace("{note_id}", String(visit.id));

        const updatePayload: Record<string, any> = {
          context: updatedContext,
          ...buildDateUpdatePayload(effectiveDocType, newStartDateTime),
        };

        await APIProvider.PATCH(updateUrl, updatePayload, {
          Authorization: `Bearer ${authToken}`,
        });

        message.success("Visit time updated and notification sent");
      } else {
        throw new Error(
          reminderResponse.message || "Failed to send reschedule notification"
        );
      }
    } else {
      // No recipients - just update the note time without sending notifications
      const updateUrl = APIRoutes.apiUpdateVisitNote
        .replace("{clinic_id}", String(clinicId))
        .replace("{patient_id}", String(visit.patientId))
        .replace("{note_id}", String(visit.id));

      // Build updated schedule data (preserve existing if any)
      const updatedSchedule = {
        ...(scheduleContext || {}),
        scheduled_date: newStartDateTime,
        scheduled_end_date: newEndDateTime,
        timezone: clinicTimezone || undefined,
      };

      // Remove old root-level schedule fields and use new structure
      const {
        scheduled_date: _sd,
        scheduled_end_date: _sed,
        scheduling: _sched,
        ...cleanContext
      } = context;

      updatedContext = {
        ...cleanContext,
        schedule: updatedSchedule,
      };

      const updatePayload: Record<string, any> = {
        context: updatedContext,
        ...buildDateUpdatePayload(effectiveDocType, newStartDateTime),
      };

      await APIProvider.PATCH(updateUrl, updatePayload, {
        Authorization: `Bearer ${authToken}`,
      });

      message.success("Visit time updated");
    }

    const updatedVisit: IVisitNoteForUpdate = {
      ...visit,
      visitDate: newStartDateTime,
      context: updatedContext,
    };

    if (onSuccess) {
      onSuccess(updatedVisit);
    }

    return { success: true, updatedVisit };
  } catch (error) {
    console.error("[UpdateVisitTime] Error:", error);
    const errorMessage =
      error instanceof Error ? error.message : "Failed to update visit time";
    message.error(errorMessage);
    return { success: false, error: errorMessage };
  }
};
